/**
 * receiptOcr.js
 * --------------
 * Browser-side OCR + parser for expense receipts.
 *
 * Uses Tesseract.js (WASM, fully client-side, no API calls/costs) to read
 * text from a captured receipt canvas, then extracts the fields the
 * expense form needs: amount, vat, date, vendor, invoice number,
 * description.
 *
 * The Tesseract worker downloads its trained data once (Arabic + English),
 * then is cached by the browser for every future receipt.
 */
import { createWorker } from "tesseract.js";

let _workerPromise = null;

async function getWorker() {
  if (_workerPromise) return _workerPromise;
  _workerPromise = (async () => {
    const w = await createWorker(["ara", "eng"], 1, {
      // Tesseract.js v6 default CDN is fine — no extra config needed.
      // logger: m => console.log("[ocr]", m),
    });
    return w;
  })();
  return _workerPromise;
}

/**
 * Run OCR on a canvas element (or any input Tesseract accepts) and return
 * the raw text + parsed fields.
 * Returns { text, fields } where fields = { amount, vat_amount, expense_date,
 * vendor, invoice_no, description }. Any missing field is `null`.
 */
export async function ocrReceipt(input) {
  const w = await getWorker();
  const { data } = await w.recognize(input);
  const text = data.text || "";
  return { text, fields: parseReceiptText(text) };
}

/** Cleanup helper (call on component unmount / page change). */
export async function terminateOcrWorker() {
  if (!_workerPromise) return;
  try {
    const w = await _workerPromise;
    await w.terminate();
  } catch { /* ignore */ }
  _workerPromise = null;
}

// ────────────────────────────────────────────────────────────────────
// Field extraction
// ────────────────────────────────────────────────────────────────────
const NUM_RE = /(\d{1,3}(?:[.,\s]\d{3})+(?:[.,]\d{1,3})?|\d+[.,]\d{1,3}|\d+)/g;

const toFloat = (s) => {
  if (!s) return null;
  // remove thousand separators (both , and space) then normalise decimal
  const cleaned = String(s).replace(/[\s,]/g, (m, _i, full) =>
    // if comma is followed by exactly 2 digits and end of string → decimal
    m === "," && /,\d{1,2}$/.test(full) ? "." : ""
  );
  const n = parseFloat(cleaned.replace(/[^\d.]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const arabicDigits = "٠١٢٣٤٥٦٧٨٩";
const normaliseDigits = (s) =>
  (s || "").replace(/[٠-٩]/g, (d) => String(arabicDigits.indexOf(d)));

/** Pick the line containing a keyword (Arabic or English). */
function lineWith(text, keywords) {
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    const low = line.toLowerCase();
    for (const kw of keywords) {
      if (low.includes(kw.toLowerCase())) return line;
    }
  }
  return null;
}

function extractAmount(text) {
  // The expense form expects `amount` = subtotal BEFORE VAT (because
  // total_with_vat is computed as amount + vat). So we prefer the
  // explicit "Subtotal" line; if none is found, we fall back to
  // (grand_total - vat) when both numbers are known.
  const norm = normaliseDigits(text);
  const lines = norm.split(/\r?\n/);

  const SUBTOTAL_KW = [
    "المجموع الفرعي", "الفرعي", "subtotal", "sub total", "sub-total",
    "net amount", "before vat", "قبل الضريبة",
  ];
  const TOTAL_KW = [
    "الإجمالي", "اجمالي", "المجموع", "إجمالي شامل", "اجمالي مع",
    "total", "amount due", "net total", "grand total",
  ];

  const pickMax = (kwList) => {
    const cands = [];
    for (const line of lines) {
      const low = line.toLowerCase();
      if (!kwList.some(kw => low.includes(kw))) continue;
      const matches = line.match(NUM_RE);
      if (!matches) continue;
      matches.map(toFloat).filter(n => n !== null).forEach(n => cands.push(n));
    }
    return cands.length ? Math.max(...cands) : null;
  };

  const subtotal = pickMax(SUBTOTAL_KW);
  if (subtotal !== null) return subtotal;
  const grandTotal = pickMax(TOTAL_KW);
  if (grandTotal === null) return null;
  // Try grand_total − vat
  const vat = extractVat(text);
  if (vat !== null && vat < grandTotal) return Math.round((grandTotal - vat) * 100) / 100;
  return grandTotal;
}

function extractVat(text) {
  const norm = normaliseDigits(text);
  const vatLine = lineWith(norm, [
    "ضريبة", "ض.ق.م", "ضريبة القيمة",
    "VAT", "TAX", "Vat",
  ]);
  if (!vatLine) return null;
  const matches = vatLine.match(NUM_RE);
  if (!matches) return null;
  const nums = matches.map(toFloat).filter((n) => n !== null && n > 0);
  if (!nums.length) return null;
  // If the line carries multiple numbers, the standard tax rate values
  // (15 / 5) are almost certainly the rate label — pick the larger
  // remaining number as the VAT amount.
  if (nums.length > 1) {
    const noRates = nums.filter((n) => !(n === 15 || n === 5));
    if (noRates.length) return Math.max(...noRates);
  }
  // Single number: take it at face value (it's the VAT amount).
  return Math.max(...nums);
}

function extractDate(text) {
  const norm = normaliseDigits(text);
  // ISO yyyy-mm-dd
  let m = norm.match(/\b(20\d{2})[-/](\d{1,2})[-/](\d{1,2})\b/);
  if (m) return `${m[1]}-${m[2].padStart(2, "0")}-${m[3].padStart(2, "0")}`;
  // dd/mm/yyyy or dd-mm-yyyy
  m = norm.match(/\b(\d{1,2})[/-](\d{1,2})[/-](20\d{2})\b/);
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  // dd month yyyy (Arabic month names)
  const months = {
    "يناير": "01", "فبراير": "02", "مارس": "03", "أبريل": "04", "ابريل": "04",
    "مايو": "05", "يونيو": "06", "يوليو": "07", "أغسطس": "08", "اغسطس": "08",
    "سبتمبر": "09", "أكتوبر": "10", "اكتوبر": "10", "نوفمبر": "11", "ديسمبر": "12",
    "Jan": "01", "Feb": "02", "Mar": "03", "Apr": "04", "May": "05", "Jun": "06",
    "Jul": "07", "Aug": "08", "Sep": "09", "Oct": "10", "Nov": "11", "Dec": "12",
  };
  for (const [name, num] of Object.entries(months)) {
    const re = new RegExp(`(\\d{1,2})\\s*${name}[a-zA-Z\\u0621-\\u064A]*\\s*(20\\d{2})`, "i");
    const r = norm.match(re);
    if (r) return `${r[2]}-${num}-${r[1].padStart(2, "0")}`;
  }
  return null;
}

function extractVendor(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  // Heuristic: vendor is usually one of the first 3 lines, has letters
  // and is NOT obviously an address/phone/tax-id.
  for (const line of lines.slice(0, 5)) {
    if (/^[\d\W_]+$/.test(line)) continue;            // pure digits / punct
    if (line.length < 3 || line.length > 60) continue;
    if (/(VAT|ضريبة|tax|invoice|فاتورة|رقم|tel|هاتف|address|عنوان)/i.test(line)) continue;
    return line;
  }
  return null;
}

function extractInvoiceNo(text) {
  const norm = normaliseDigits(text);
  // Patterns: "Invoice #: 123", "Invoice No 123", "فاتورة رقم 123", "INV-123"
  const re1 = /(?:invoice|inv|فاتورة|رقم الفاتورة|رقم)[\s#:.-]*([A-Z0-9-]{3,20})/i;
  const m1 = norm.match(re1);
  if (m1 && /\d/.test(m1[1])) return m1[1];
  // Standalone "INV-12345" type code
  const re2 = /\b([A-Z]{2,5}-\d{3,8})\b/;
  const m2 = norm.match(re2);
  if (m2) return m2[1];
  return null;
}

function extractDescription(text) {
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  // Pick the line that looks like an item description: contains letters,
  // not the vendor (first line), not a number-heavy line.
  const skip = /(vat|ضريبة|tax|total|إجمالي|اجمالي|subtotal|invoice|فاتورة|date|تاريخ|tel|هاتف)/i;
  for (const line of lines.slice(1, 12)) {
    if (skip.test(line)) continue;
    if (/^[\d\W_]+$/.test(line)) continue;
    if (line.length < 4 || line.length > 80) continue;
    const letters = (line.match(/[A-Za-z\u0621-\u064A]/g) || []).length;
    if (letters >= 3) return line;
  }
  return null;
}

export function parseReceiptText(text) {
  return {
    amount:        extractAmount(text),
    vat_amount:    extractVat(text),
    expense_date:  extractDate(text),
    vendor:        extractVendor(text),
    invoice_no:    extractInvoiceNo(text),
    description:   extractDescription(text),
  };
}
